// components/DocumentViewer.jsx - Fixed version for flexible height
import React, { useState, useEffect, useRef } from 'react';

/**
 * Enhanced DocumentViewer component with truly flexible height
 * @param {Object} props
 * @param {string} props.documentUrl - URL of the document to display
 * @param {string} props.title - Title of the document
 * @param {string} props.documentType - Type of document (pdf, image, etc.)
 * @param {number} props.initialHeight - Initial height in pixels (default: 600)
 * @param {boolean} props.showPageControls - Whether to show page navigation controls
 */
const DocumentViewer = ({
  documentUrl,
  title = 'Document',
  documentType = '',
  initialHeight = 600,
  showPageControls = true,
  flexibleHeight = false
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewerHeight] = useState(initialHeight); // Track viewer height separately
  const [contentType, setContentType] = useState(null); // Track actual content type

  const iframeRef = useRef(null);
  const viewerRef = useRef(null);
  
  // Fetch content type from URL
  useEffect(() => {
    if (documentUrl) {
      fetch(documentUrl, { method: 'HEAD' })
        .then(response => {
          const type = response.headers.get('Content-Type');
          console.log('Fetched Content-Type:', type);
          setContentType(type);
        })
        .catch(err => {
          console.log('Could not fetch content type:', err);
          setContentType(null);
        });
    }
  }, [documentUrl]);

  // Effect for height adjustment based on window size

  // Effect to handle iframe loading
  useEffect(() => {
    // Set initial loading state
    setIsLoading(true);
    
    // Function to handle iframe load event
    const handleIframeLoad = () => {
      console.log('Document iframe loaded');
      setIsLoading(false);
      
      // Try to determine total pages for PDFs
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          // For PDF.js viewers
          if (iframe.contentWindow.PDFViewerApplication) {
            const pdfViewer = iframe.contentWindow.PDFViewerApplication;
            pdfViewer.initializedPromise.then(() => {
              setTotalPages(pdfViewer.pagesCount || 1);
            });
          }
          
          // For other PDF viewers that expose page count
          else if (iframe.contentWindow.document.querySelector('.page-count')) {
            const pageCountElement = iframe.contentWindow.document.querySelector('.page-count');
            const pageCount = parseInt(pageCountElement.textContent, 10);
            if (!isNaN(pageCount)) {
              setTotalPages(pageCount);
            }
          }
        }
      } catch (error) {
        console.log('Could not determine PDF pages count:', error);
      }
    };
    
    // Add load event listener to iframe
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
      
      // Clean up on unmount
      return () => {
        iframe.removeEventListener('load', handleIframeLoad);
      };
    }
  }, [documentUrl]);
  
  // Toggle fullscreen view
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  // Handle zoom in
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 20, 200));
  };
  
  // Handle zoom out
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 20, 50));
  };
  
  // Reset zoom
  const handleZoomReset = () => {
    setZoom(100);
  };
  
  // Handle page navigation
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      
      // Attempt to change page in PDF viewers
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          // For PDF.js viewers
          if (iframe.contentWindow.PDFViewerApplication) {
            const pdfViewer = iframe.contentWindow.PDFViewerApplication;
            pdfViewer.page = currentPage - 1;
          }
        }
      } catch (error) {
        console.log('Could not change PDF page');
      }
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      
      // Attempt to change page in PDF viewers
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          // For PDF.js viewers
          if (iframe.contentWindow.PDFViewerApplication) {
            const pdfViewer = iframe.contentWindow.PDFViewerApplication;
            pdfViewer.page = currentPage + 1;
          }
        }
      } catch (error) {
        console.log('Could not change PDF page');
      }
    }
  };
  
  // Determine document icon/type label
  const getDocumentTypeIcon = () => {
    if (documentType === 'pdf' || documentUrl?.toLowerCase().endsWith('.pdf')) {
      return '📄';
    } else if (
      documentType === 'image' || 
      documentUrl?.toLowerCase().match(/\.(jpe?g|png|gif|bmp)$/i)
    ) {
      return '🖼️';
    } else if (
      documentType === 'word' || 
      documentUrl?.toLowerCase().match(/\.(docx?|rtf)$/i)
    ) {
      return '📝';
    }
    
    return '📄';
  };
  
  const getDocumentTypeLabel = () => {
    if (documentType === 'pdf' || documentUrl?.toLowerCase().endsWith('.pdf')) {
      return 'PDF';
    } else if (
      documentType === 'image' || 
      documentUrl?.toLowerCase().match(/\.(jpe?g|png|gif|bmp)$/i)
    ) {
      return 'IMAGE';
    } else if (
      documentType === 'word' || 
      documentUrl?.toLowerCase().match(/\.(docx?|rtf)$/i)
    ) {
      return 'WORD';
    }
    
    return documentType?.toUpperCase() || 'DOCUMENT';
  };
  
  // Check if document is an image based on multiple criteria
  const isImage =
    documentType === 'image' ||
    documentUrl?.toLowerCase().match(/\.(jpe?g|png|gif|bmp|webp)$/i) ||
    contentType?.startsWith('image/');

  // Debug logging
  useEffect(() => {
    console.log('DocumentViewer - URL:', documentUrl);
    console.log('DocumentViewer - Type:', documentType);
    console.log('DocumentViewer - Content-Type:', contentType);
    console.log('DocumentViewer - isImage:', isImage);
  }, [documentUrl, documentType, contentType, isImage]);

  // If no document URL is provided
  if (!documentUrl) {
    return (
      <div
        className={`rounded-lg overflow-hidden bg-white shadow-md flex flex-col relative border border-gray-200 transition-all duration-300 justify-center items-center ${
          flexibleHeight ? 'h-full' : 'mb-6 min-h-[500px] max-h-[80vh]'
        }`}
        ref={viewerRef}
        style={flexibleHeight ? {} : {height: `${viewerHeight}px`}}
      >
        <div className="flex flex-col items-center justify-center h-full bg-gray-50 text-gray-600 text-lg text-center p-6">
          <i className="text-4xl mb-2">📄</i>
          <p>No document available</p>
        </div>
      </div>
    );
  }
  
  // Document viewer content
  const viewerContent = (
    <>
      <div className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-100 to-white border-b border-gray-200 h-15 min-h-[60px] z-[5]">
        <div className="flex items-center min-w-[100px]">
          <span className="text-lg mr-2">{getDocumentTypeIcon()}</span>
          <span className="font-semibold text-sm px-2 py-1 bg-blue-600 text-white rounded-full uppercase tracking-wide">{getDocumentTypeLabel()}</span>
        </div>
        
        {showPageControls && getDocumentTypeLabel() === 'PDF' && (
          <div className="flex items-center gap-2 mr-4">
            <button
              className="bg-white border border-gray-300 rounded w-9 h-9 flex items-center justify-center cursor-pointer font-bold text-blue-600 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
              aria-label="Previous Page"
            >
              ◀
            </button>
            <span className="text-sm text-gray-700 font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              className="bg-white border border-gray-300 rounded w-9 h-9 flex items-center justify-center cursor-pointer font-bold text-blue-600 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              aria-label="Next Page"
            >
              ▶
            </button>
          </div>
        )}
        
        <div className="flex items-center gap-2">
          <button
            className="bg-white border border-gray-300 rounded w-9 h-9 flex items-center justify-center cursor-pointer font-bold text-blue-600 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleZoomOut}
            title="Zoom Out"
            disabled={zoom <= 50}
            aria-label="Zoom Out"
          >
            －
          </button>
          <span className="text-sm text-gray-700 font-medium min-w-[60px] text-center">{zoom}%</span>
          <button
            className="bg-white border border-gray-300 rounded w-9 h-9 flex items-center justify-center cursor-pointer font-bold text-blue-600 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleZoomIn}
            title="Zoom In"
            disabled={zoom >= 200}
            aria-label="Zoom In"
          >
            ＋
          </button>
          <button
            className="bg-white border border-gray-300 rounded w-9 h-9 flex items-center justify-center cursor-pointer font-bold text-blue-600 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:border-blue-600"
            onClick={handleZoomReset}
            title="Reset Zoom"
            aria-label="Reset Zoom"
          >
            ↺
          </button>
        </div>
        
        <button
          className="bg-white border border-gray-300 rounded w-9 h-9 flex items-center justify-center cursor-pointer font-bold text-blue-600 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:border-blue-600"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          aria-label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
        >
          {isFullscreen ? "⤓" : "⤢"}
        </button>
      </div>
      
      <div className={`flex-1 overflow-auto relative bg-gray-50 flex items-center justify-center ${
        flexibleHeight ? 'h-full' : 'min-h-[350px]'
      }`}>
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-10">
            <div className="w-12 h-12 border-4 border-blue-100 border-l-blue-600 rounded-full animate-spin mb-4"></div>
            <div className="text-base text-blue-600 font-medium">Loading document...</div>
          </div>
        )}

        {/* Render images directly instead of in iframe */}
        {isImage ? (
          <div className={`w-full h-full flex items-center justify-center ${flexibleHeight ? '' : 'p-4'}`}>
            <img
              src={documentUrl}
              alt={title}
              className="max-w-full max-h-full object-contain"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center'
              }}
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            />
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={documentUrl}
            title={title}
            className="w-full h-full border-none block min-h-[500px]"
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top left',
              width: `${100 * (100 / zoom)}%`,
              height: `${100 * (100 / zoom)}%`
            }}
          />
        )}
      </div>
    </>
  );
  
  // Render the component
  return isFullscreen ? (
    <div className="fixed inset-0 bg-black bg-opacity-85 z-[1050] flex items-center justify-center backdrop-blur">
      <div className="bg-white rounded-lg w-[95%] h-[95%] flex flex-col overflow-hidden shadow-2xl">
        <div className="flex justify-between items-center p-4 bg-gray-100 border-b border-gray-200 h-15">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors text-xl font-bold"
            onClick={toggleFullscreen}
            aria-label="Close fullscreen view"
          >
            ×
          </button>
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          {viewerContent}
        </div>
      </div>
    </div>
  ) : (
    <div
      className={`rounded-lg overflow-hidden bg-white shadow-md flex flex-col relative border border-gray-200 transition-all duration-300 ${
        flexibleHeight ? 'h-full' : 'mb-6 min-h-[500px] max-h-[80vh]'
      }`}
      ref={viewerRef}
      style={flexibleHeight ? {} : {
        height: `${viewerHeight}px`
      }}
    >
      {viewerContent}
    </div>
  );
};

export default DocumentViewer;